import { useEffect, useState } from "react";
import { useRouteLoaderData, useSearchParams } from "react-router-dom";
import { User } from "../../services/user/userModel";
import IdentitySection from "./IdentitySection";
import PasswordSection from "./PasswordSection";
import PremiumSection from "./PremiumSection";
import PremiumSponsorshipSection from "./PremiumSponsorshipSection";
import { ReturnButton } from "../../components/ReturnButton";
import { cx } from "../../react-helpers/css";
import { useTranslation } from "react-i18next";
import { PremiumSponsorship } from "../../services/premiumSponsorship/premiumSponsorshipModel";
import { premiumSponsorshipService } from "../../services/premiumSponsorship/premiumSponsorshipService";
import { toastsWithIntl } from "../../services/toastService";

const { getMyPremiumSponsorships } = premiumSponsorshipService();
const { toastError } = toastsWithIntl(["accounts"]);

const AccountPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loggedUser } = useRouteLoaderData("logged-user") as {
    loggedUser: User;
  };

  const isOrganization = loggedUser.organizationId !== null;
  const [mySponsorships, setMySponsorships] = useState<PremiumSponsorship[]>(
    [],
  );

  const reloadSponsorships = () => {
    if (isOrganization) return;
    getMyPremiumSponsorships()
      .then(setMySponsorships)
      .catch(() => undefined);
  };

  useEffect(() => {
    reloadSponsorships();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrganization]);

  // Set by the login form when the candidate followed a sponsorship
  // invitation but signed in with another one of their addresses: the
  // sponsorship stayed untouched server-side, which is otherwise silent.
  const mismatchedSponsorshipEmail = searchParams.get(
    "sponsorshipEmailMismatch",
  );
  useEffect(() => {
    if (!mismatchedSponsorshipEmail) return;
    toastError("accounts:premium-sponsorship.accept.EMAIL_MISMATCH", {
      replace: { email: mismatchedSponsorshipEmail },
      id: "sponsorship-email-mismatch",
    });
    setSearchParams(
      (prev) => {
        prev.delete("sponsorshipEmailMismatch");
        return prev;
      },
      { replace: true },
    );
  }, [mismatchedSponsorshipEmail, setSearchParams]);

  const hasActiveSponsorshipGrant = mySponsorships.some(
    (sponsorship) =>
      sponsorship.status === "accepted" && sponsorship.premiumGranted === true,
  );
  const displayPremium =
    !isOrganization &&
    loggedUser.subscriptionEndDate &&
    !hasActiveSponsorshipGrant;

  return (
    <div
      className={cx([
        "layout_content",
        isOrganization ? "--bg--organization-dashboard" : "--bg--candidate",
      ])}
    >
      <div className="page-content container --wrap--m">
        <div>
          <ReturnButton>{t("accounts:GO_BACK_BUTTON")}</ReturnButton>
        </div>
        <div className={"grid lblock--l"}>
          <IdentitySection user={loggedUser} />
          <PasswordSection />
          {displayPremium && <PremiumSection user={loggedUser} />}
          {!isOrganization && (
            <PremiumSponsorshipSection
              sponsorships={mySponsorships}
              onChange={reloadSponsorships}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
